import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState, useReducer } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ampli } from '../../ampli';
import debounce from 'lodash.debounce';
import { GraphWidget } from '../../components/GraphWidget/GraphWidget';
import { useSavedGraph } from '../../components/GraphWidget/useSavedGraph';
import { GraphViewOverlay } from '../SocialGraphPOC/GraphViewOverlay';

import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import { EMPTY_ACTORS_RESULT, showActorSearchError } from '../../utils/actorSearchError';
import { toApiAffiliationTypes } from '../../utils/affiliationTypes';
import { Loader } from '../../components/Loader/Loader';
import { SourcesTable } from '../../components/SourcesTable/SourcesTable';
import { Search } from '../../components/Search/Search';
import { CheckboxField } from '../../components/Checkbox/CheckboxField';
import {ReactComponent as EditIcon} from '../../assets/edit.svg';
import { GroupEditModal } from '../GroupsPage/GroupEditModal';
import Tooltip from '../../components/Tooltip/Tooltip';
import styles from './GroupPage.module.scss';
import tableStyles from '../../components/SourcesTable/SourcesTable.module.scss'
import { useCurrentUser } from '../../contexts/CurrentUser';
import CopyGroupToWorkspacePopup from "../../components/CopyGroupToWorkspacePopup";
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { ReactComponent as CloseIcon } from '../SearchPage/assets/close.svg';
import { ReactComponent as AddToIcon } from '../../assets/addTo.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete.svg';
import { ReactComponent as DeleteIconNew } from '../../assets/delete-new.svg';
import { Filter } from '../SourcesPage/Filter/Filter';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { Radiobutton } from '../../components/DropdownMenu/Radiobutton';
import { SortingOrder } from '../../components/DropdownMenu/SortingOrder';
import { ReactComponent as ArrowIcon } from '../NarrativePage/assets/arrow.svg';
import { ReactComponent as ArrowDownIcon } from '../NarrativePage/assets/arrowDown.svg';
import { ReactComponent as SortIcon } from '../../assets/sort.svg';
import bulkPanelStyles from '../NarrativePage/NarrativePage.module.scss';
import { AddToSourceGroupModal } from '../../components/AddToSourceGroupModal/AddToSourceGroupModal';
import Modal from '../../components/Modal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import websocketConnection from '../../websocket';

const sortingFieldNames = {
  ID: 'id',
  AUDIENCE: 'audience',
  ACTOR: 'name',
  PLATFORM: 'source_type',
  STATUS: 'status',
  URL: 'url',
  COUNTRY: 'country',
  RELEVANCE: 'relevance',
};

const sortingFieldNamesBack = {
  'id': 'ID',
  'audience': 'AUDIENCE',
  'name': 'ACTOR',
  'source_type': 'PLATFORM',
  'status': 'STATUS',
  'url': 'URL',
  'country': 'COUNTRY',
  'relevance': 'RELEVANCE',
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'APPLY_FILTER': {
      return Object.assign({}, state, {
        platforms: action.payload.platforms,
        countries: action.payload.countries,
        audience_lte: action.payload.audience_lte,
        audience_gte: action.payload.audience_gte,
        status: action.payload.status,
        affiliationCountries: action.payload.affiliationCountries,
        IoCs: action.payload.IoCs,
        showOnlyBots: action.payload.showOnlyBots,
        sourceTags: action.payload.sourceTags,
        affiliationCountriesExclude: action.payload.affiliationCountriesExclude,
        countriesExclude: action.payload.countriesExclude,
        audienceExclude: action.payload.audienceExclude,
        geoGroup: action.payload.geoGroup,
        geoGroupExclude: action.payload.geoGroupExclude,
        botsExclude: action.payload.botsExclude,
        IoCsExclude: action.payload.IoCsExclude,
        sourceTagsExclude: action.payload.sourceTagsExclude,
        affiliationTypes: action.payload.affiliationTypes,
        affiliationTypesExclude: action.payload.affiliationTypesExclude,
        page: action.payload.page
      })
    }

    case 'UPDATE_QUERY': {
      return Object.assign({}, state, {
        searchQuery: action.payload,
      })
    }

    case 'SORT_BY': {
      return Object.assign({}, state, {
        sorting: {...state.sorting, fieldName: action.fieldName}
      })
    }

    case 'IS_ASCENDING': {
      return Object.assign({}, state, {
        sorting: {...state.sorting, isAscending: action.isAscending}
      })
    }

    case 'CHANGE_PAGE': 
    return Object.assign({}, state, {
      page: action.page,
    })
    default: 
      return state;
  }
}

export function GroupPage() {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const showGraphWidget = currentUser?.workspace?.config?.featureflags?.includes('social_graph');
  const { groupId } = useParams();
  const graphWidget = useSavedGraph('actor_group', groupId, Boolean(showGraphWidget));
  // graph opens in an overlay (not a route) so closing it keeps the user's
  // place on this page — scroll position, tabs, filters
  const [isGraphOverlayOpen, setIsGraphOverlayOpen] = useState(false);
  const [group, setGroup] = useState(null);
  const [sources, setSources] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editModal, setEditModal] = useState({isOpen: false, group: null});
  const [error, setError] = useState(null);
  const [copyGroupModal, setCopyGroupModal] = useState({
    isActive: false,
    workspaceId: null,
  });
  const [selectedSources, setSelectedSources] = useState([]);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({isActive: false, group: null, ids: []});
  const [copySelectedToGroupModal, setCopySelectedToGroupModal] = useState({isActive: false, group: null, ids: []});

  const [state, dispatch] = useReducer(reducer, {
    sorting: {isAscending: false, fieldName: sortingFieldNamesBack['relevance']},
    searchQuery: searchParams.get('q') ||'',
    platforms: [],
    countries: [],
    audience_lte: null,
    audience_gte: null,
    status: [],
    affiliationCountries: [],
    IoCs: [],
    showOnlyBots: false,
    sourceTags: [],
    affiliationCountriesExclude: false,
    countriesExclude: false,
    audienceExclude: false,
    geoGroup: [],
    geoGroupExclude: false,
    botsExclude: false,
    IoCsExclude: false,
    sourceTagsExclude: false,
    affiliationTypes: [],
    affiliationTypesExclude: false,
    page: searchParams.get('page') || 1
  });

  const page = searchParams.get('page') ?? 1;
  const searchQuery = searchParams.get('q');
  const [dropdownMenu, setDropdownMenu] = useState(false);

  useEffect(() => {
    API.fetch('GET', `/API/v1/groups/${groupId}`).then(setGroup).catch(error => {
      setGroup(null);
      if (error.message === 'Client error: Group not found') {
        setError(t('Group not found'));
      } else {
        setError(t('Failed to fetch group'));
        showRequestError(error, t('Actor group'));
      }
    });
  }, [groupId]);

  useEffect(() => {
    const handleGroupPopulationProgress = (data) => {
      if (data?.object_id !== group?.id) {
        return;
      }

      try {
        setGroup((group) => ({
          ...group,
          population_status: data?.data?.status,
        }));

        if (data?.data?.status === 'DONE') {
          getSources(
            state.searchQuery,
            state.platforms,
            state.countries,
            state.audience_lte,
            state.audience_gte,
            state.status,
            state.affiliationCountries,
            state.IoCs,
            state.showOnlyBots,
            state.sourceTags,
            state.affiliationCountriesExclude,
            state.countriesExclude,
            state.audienceExclude,
            state.geoGroup,
            state.geoGroupExclude,
            state.botsExclude,
            state.IoCsExclude,
            state.sourceTagsExclude,
            state.affiliationTypes,
            state.affiliationTypesExclude,
            state.sorting,
            page
          );
        }
      } catch {}
    };

    websocketConnection.addHandler('GROUP_POPULATION_STATUS', handleGroupPopulationProgress);

    return () => {
      websocketConnection.removeHandler('GROUP_POPULATION_STATUS');
    }
  }, [group]);


  const getSources = useCallback((
    searchQuery,
    platforms,
    countries,
    audience_lte,
    audience_gte,
    status,
    affiliationCountries,
    IoCs,
    showOnlyBots,
    sourceTags,
    affiliationCountriesExclude,
    countriesExclude,
    audienceExclude,
    geoGroup,
    geoGroupExclude,
    botsExclude,
    IoCsExclude,
    sourceTagsExclude,
    affiliationTypes,
    affiliationTypesExclude,
    sorting,
    page=1
  ) => {
    const urlParams = new URLSearchParams();
    urlParams.set('size', '100');
    urlParams.set('page', page);

    let sortingQuery = sorting?.fieldName
    ? `${sorting?.isAscending ? '' : '-'}${sortingFieldNames[sorting?.fieldName]}`
    : null;

    setSources(null);

    if (searchQuery) {
      urlParams.set('q', searchQuery);
    }
  
    if (platforms?.length) {
      platforms.forEach((platform) => {
        urlParams.append('source_types', platform);
      });
    }
  
    if (countries?.length) {
      countries.forEach((country) => {
        urlParams.append('origin_country_ids', country);
      });
    }
  
    if(audience_lte !== null && audience_lte?.length > 0) {
      urlParams.set('audience_lte', audience_lte);
    }
  
    if(audience_gte !== null && audience_gte?.length > 0) {
      urlParams.set('audience_gte', audience_gte);
    }
  
    if(status?.length) {
      status.forEach((status_) => {
        urlParams.append('statuses', status_);
      });
    }
  
    if (affiliationCountries?.length) {
      affiliationCountries.forEach((country) => {
        urlParams.append('country_ids', country);
      });
    }
  
    if (IoCs?.length) {
      IoCs.forEach((IoC) => {
        urlParams.append('entity_types', IoC);
      });
    }
  
    if (showOnlyBots) {
      urlParams.set('is_bot', showOnlyBots);
    }

    if (geoGroup?.length) {
      geoGroup?.forEach((geoGroup) => {
        urlParams.append('country_group_ids', geoGroup);
      });
    }
  
    let excludedFilters = [];

    if (sourceTags?.length) {
      sourceTags.forEach(tag => {
        urlParams.append('tags', tag)
      })
      if (sourceTagsExclude) {
        excludedFilters.push('tags');
      }
    }

    if (affiliationTypes?.length) {
      toApiAffiliationTypes(affiliationTypes).forEach((type) => {
        urlParams.append('affiliation_types', type);
      });
    }
    if (countriesExclude === true) {
      excludedFilters.push('origin_country_ids');
    }

    if (affiliationCountriesExclude === true) {
      excludedFilters.push('country_ids');
    }

    if (audienceExclude === true) {
      excludedFilters.push('audience_lte', 'audience_gte');
    }

    if (botsExclude === true) {
      excludedFilters.push('source_bots');
    }

    if (geoGroupExclude) {
      excludedFilters.push('country_group_ids');
    }

    if (IoCsExclude === true && IoCs?.length) {
      excludedFilters.push('entity_types');
    }

    if (affiliationTypesExclude === true && affiliationTypes?.length) {
      excludedFilters.push('affiliation_types');
    }

    if (excludedFilters.length > 0) {
      excludedFilters.forEach((filter) => {
        urlParams.append('exclusive_filters', filter);
      });
    }

    if (sorting) {
      if (Array.isArray(sorting?.fieldName)) {
        sorting.fieldName.forEach((element) => {
          urlParams.append(
            'sorting',
            `${sorting?.isAscending ? '' : '-'}${element}`,
          );
        });
      } else {
        urlParams.set('sorting', sortingQuery);
      }
    }

    urlParams.set('source_group_id', groupId);

    API.fetch('GET', `/API/v2/actors?${urlParams.toString()}`).then(setSources).catch((e) => {
      if (showActorSearchError(e, t('Actors'))) setSources(EMPTY_ACTORS_RESULT);
    });
  } , [groupId])

  useEffect(() => {
    if(selectedSources.length > 0) {
      setSelectedSources([]);
    }
    getSources(
      state.searchQuery,
      state.platforms,
      state.countries,
      state.audience_lte,
      state.audience_gte,
      state.status,
      state.affiliationCountries,
      state.IoCs,
      state.showOnlyBots,
      state.sourceTags,
      state.affiliationCountriesExclude,
      state.countriesExclude,
      state.audienceExclude,
      state.geoGroup,
      state.geoGroupExclude,
      state.botsExclude,
      state.IoCsExclude,
      state.sourceTagsExclude,
      state.affiliationTypes,
      state.affiliationTypesExclude,
      state.sorting,
      page
    );
  }, [state, page, getSources]);

  const checkIsSelected = (source) => {
    return selectedSources.some((item) => item.id === source.id);
  };

  const selectedSourcesHandler = (source, value) => {
    if (value) {
      setSelectedSources([...selectedSources, source]);
    } else {
      setSelectedSources(selectedSources.filter((item) => item.id !== source.id));
    }
  };

  const clearAllSelectedSources = () => {
    setSelectedSources([]);
  };

  const areAllIdsSelected = (sources, selectedSources) => {
    if (!sources?.length) return false;
    return sources.every((source) => selectedSources.some((item) => item.id === source.id));
  };

  const toggleAll = (sources, selectedSources) => {
    if (areAllIdsSelected(sources, selectedSources)) {
      setSelectedSources([]);
    } else {
      setSelectedSources(sources);
    }
  };

  const handleDeleteSources = () => {
    const sourceIds = deleteConfirmModal.ids;
    API.fetch('DELETE', `/API/v1/groups/${groupId}/sources`, null, {
      source_ids: sourceIds,
    }).then(() => {
      setDeleteConfirmModal({ isActive: false, group: null, ids: [] });
      setSelectedSources([]);
      getSources(
        state.searchQuery,
        state.platforms,
        state.countries,
        state.audience_lte,
        state.audience_gte,
        state.status,
        state.affiliationCountries,
        state.IoCs,
        state.showOnlyBots,
        state.sourceTags,
        state.affiliationCountriesExclude,
        state.countriesExclude,
        state.audienceExclude,
        state.geoGroup,
        state.geoGroupExclude,
        state.botsExclude,
        state.IoCsExclude,
        state.sourceTagsExclude,
        state.affiliationTypes,
        state.affiliationTypesExclude,
        state.sorting,
        page
      );
    }).catch((e) => showRequestError(e, t('Remove actors')));
  };

  const buttonsOnScroll = () => {
    if(Object.keys(selectedSources).length > 0) {
      const buttons = document.getElementById('bulk');
      let scrolly = window.pageYOffset;
      if (!buttons) {
        return
      }

      if(scrolly > 150) {
        buttons.classList.add(bulkPanelStyles.floating)
      } else {
        buttons.classList.remove(bulkPanelStyles.floating)
      }
    } else {
      return
    }
  }

  useEffect(() => {
      window.addEventListener('scroll', buttonsOnScroll);
      window.addEventListener('mousemove', buttonsOnScroll);

    return() => {
      window.removeEventListener('scroll', buttonsOnScroll);
      window.removeEventListener('mousemove', buttonsOnScroll);
    }
  })

  // Same signature GroupEditModal calls with; returns the request promise so the
  // modal can report the outcome and close itself
  const handleEdit = (name, group, privacySettings, workspaces, description) =>
    API.fetch('PATCH', `/API/v1/groups/${group.id}`, null, {
      name: name,
      description: description,
      visibility: privacySettings,
      workspace_ids: workspaces?.map((workspace) => workspace.value),
    }).then(() => {
      API.fetch('GET', `/API/v1/groups/${groupId}`).then(setGroup).catch((e) => showRequestError(e, t('Actor group')));
    });

  const newText = group?.description?.replace(/\n+(\s*\n+)?/g, '\n').split('\n');
  const sanitizedText = newText?.map(item => DOMPurify.sanitize(item, { ALLOWED_TAGS: ['mark', 'span'],  ALLOWED_ATTR: ['class']}));

  const clean = useMemo(
    // () => DOMPurify.sanitize(newText, { ALLOWED_TAGS: ['mark'] }),
    () => {
      return sanitizedText;
    },
    [sanitizedText, group],
  );

  useEffect(() => {
    ampli.track({
      event_type: 'Open Actors Group Page',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        group_id: group?.id,
      }
    })
  }, []);

  if(!group && error) {
    return <div className='noData'>{t(error)}</div>
  }

  if (!group) {
    return <Loader />;
  }

  const sortingMenu = [
    {
      name: t('Identifier'),
      value: 'ID',
    },
    {
      name: t('Audience'),
      value: 'AUDIENCE',
    },
    {
      name: t('Platform'),
      value: 'PLATFORM',
    },
    {
      name: t('Name'),
      value: 'ACTOR',
    },
    {
      name: t('Link'),
      value: 'URL',
    },
    {
      name: t('Country'),
      value: 'COUNTRY',
    },
    {
      name: t('Status'),
      value: 'STATUS',
    },
    {
      name: t('Relevance'),
      value: 'RELEVANCE',
    },
  ]

  const sortingOrder = [
    {
      name: t('A-Z'),
      icon: <ArrowIcon/>,
      value: true,
    },
    {
      name: t('Z-A'),
      value: false,
      icon: <ArrowDownIcon/>,
    },
  ]

  return (
    <div className={cn('list-content', styles.root)}>
      <div className="page-header">
        <div className="breadcrumb">
          <span>
            <Link to="/groups">{t('Actor groups')}</Link>
          </span>
          <span>{group.name}</span>
        </div>
        <div className="controls">
          {currentUser?.is_super_admin === true ? (
            <Tooltip content={t('Copy')} position='bottom'>
              <button
                type="button"
                className={cn('new-button')}
                onClick={() =>
                  setCopyGroupModal({
                    isActive: true,
                  })
                }
              >
                <CopyIcon/>
              </button>
            </Tooltip>
          ) : (
            ''
          )}

          {group?.owned_by_workspace ? <Tooltip content={t('Edit')} position="bottom">
            <Link
              to={`/groups/${group.id}/edit`}
              className='button new-button'
            >
              <EditIcon />
            </Link>
          </Tooltip> : ''}
        </div>
      </div>

      <div className="next-card-header">
        {group?.owned_by_workspace ? <div className="row">
          <h1></h1>
          {selectedSources?.length > 0 ? (
            <div className={bulkPanelStyles.bulkButtonsWrapper} id="bulk">
              <button>
                {selectedSources?.length} {t('selected')}{' '}
                <span
                  className={bulkPanelStyles.clear}
                  onClick={() => clearAllSelectedSources()}
                >
                  <CloseIcon />
                </span>{' '}
              </button>
              <button
                onClick={() =>
                  setCopySelectedToGroupModal({
                    isActive: true,
                    sources: selectedSources,
                  })
                }
              >
                <CopyIcon />
                {t('Copy to group')}
              </button>
              <button
                onClick={() =>
                  setDeleteConfirmModal({
                    isActive: true,
                    group: group,
                    ids: selectedSources.map((source) => source.id),
                  })
                }
              >
                <DeleteIcon />
                {t('Delete')}
              </button>
            </div>
          ) : (
            ''
          )}
        </div> : ''}

        <div className={styles.descriptionCard}>
          <p className={styles.descriptionCardTitle}>{group.name}</p>
          {group?.description?.length > 0 && (
            <div className={styles.descriptionCardBody}>
              {clean.map((item, i) => <p key={`paragraph_${i}`} dangerouslySetInnerHTML={{ __html: item }}></p>)}
            </div>
          )}
        </div>

        {showGraphWidget ? (
          <div className={styles.graphWidgetWrapper}>
            <GraphWidget
              title={t('Source exploration graph')}
              description={t('Explore how actors in this group connect. Find communities, key spreaders, and links to actors beyond it.')}
              generatingHint={t('Usually takes about 1 hour. You can leave this page, we\'ll notify you when it\'s ready.')}
              status={graphWidget.status}
              generatedAt={graphWidget.generatedAt}
              stats={graphWidget.stats}
              isBusy={graphWidget.isBusy}
              showDateRange
              entityName={group?.name}
              graphName={graphWidget.graphName}
              graphStartDate={graphWidget.startDate}
              graphEndDate={graphWidget.endDate}
              onGenerate={graphWidget.generate}
              onCancel={graphWidget.cancel}
              onRegenerate={graphWidget.regenerate}
              onOpen={() => graphWidget.graphId && setIsGraphOverlayOpen(true)}
            />
          </div>
        ) : ''}

        {isGraphOverlayOpen && graphWidget.graphId ? (
          <GraphViewOverlay
            graphId={graphWidget.graphId}
            onClose={() => setIsGraphOverlayOpen(false)}
          />
        ) : ''}

        <div className={styles.searchWrapper}>
          <Search
            className={styles.searchInput}
            onChange={value => {
              dispatch({type: 'UPDATE_QUERY', payload: value});
              dispatch({type: 'CHANGE_PAGE', page: 1})
              setSearchParams({ q: value});
            }}
          />

          <DropdownMenu  
            isOpen={dropdownMenu}
            header={t('Sort by')}
            onClick={() => setDropdownMenu(!dropdownMenu)}
            buttonName={t('Sort')}
            icon={<SortIcon/>}
          >
            <Radiobutton 
              itemsList={sortingMenu}
              current={state.sorting.fieldName}
              onChange={(value) => dispatch({type: 'SORT_BY', fieldName: value})}
            />

            <SortingOrder 
              itemsList={sortingOrder}
              onClick={value => dispatch({type: 'IS_ASCENDING', isAscending: value})}
              current={state.sorting.isAscending}
            />
          </DropdownMenu>


          <Filter
            state={state}
            onChange={(
              platforms,
              countries,
              audience,
              status,
              affiliationCountries,
              IoCs,
              showOnlyBots,
              sourceTags,
              affiliationCountriesExclude,
              countriesExclude,
              audienceExclude,
              geoGroup,
              geoGroupExclude,
              botsExclude,
              IoCsExclude,
              sourceTagsExclude,
              affiliationTypes,
              affiliationTypesExclude
            ) => {
            dispatch({type: 'APPLY_FILTER',
            payload: {
              platforms: platforms,
              countries: countries,
              audience_lte: audience[1],
              audience_gte: audience[0],
              affiliationCountries: affiliationCountries,
              IoCs: IoCs,
              showOnlyBots: showOnlyBots,
              sourceTags: sourceTags,
              affiliationCountriesExclude: affiliationCountriesExclude,
              countriesExclude: countriesExclude,
              audienceExclude: audienceExclude,
              geoGroup: geoGroup,
              geoGroupExclude: geoGroupExclude,
              botsExclude: botsExclude,
              IoCsExclude: IoCsExclude,
              sourceTagsExclude: sourceTagsExclude,
              affiliationTypes: affiliationTypes,
              affiliationTypesExclude: affiliationTypesExclude,
              status: status,
              page: 1,
            }})
            if (state.searchQuery) {
              setSearchParams({ q: state.searchQuery, page: 1});
            } else {
              setSearchParams({ page: 1});
            }
          }}
          />
        </div>
        
      </div>

      {group?.population_status === 'POPULATING' ? (
          <div className={styles.populationStatus}>
            {t(
              'Please, wait till actor group population ends. This may take several minutes.',
            )}
          </div>
        ) : group?.population_status === 'FAILED' ? (
          <div className={styles.populationStatusFailed}>
            {t('Data import has failed. Try to create this group again.')}
          </div>
        ) : <SourcesTable
        rowClassName={styles.tableRow}
        sources={sources}
        withHeader={true}
        searchQuery={state.searchQuery}
        checkIsSelected={checkIsSelected}
        renderPrefixRow={(source) => {
          const isSelected = checkIsSelected(source);

          if (! group?.owned_by_workspace) {
            return <td></td>;
          }

          return (
            <td className={tableStyles.tableCheckbox}>
              <CheckboxField
              className={styles.tableRow}
              checked={isSelected}
              onChange={(value) => {selectedSourcesHandler(source, value)}}
            />
            </td>
          );
        }}
        selectAllCheckbox={() => {
          if (! group?.owned_by_workspace) {
            return <td></td>;
          }
          return (
            <td className={tableStyles.tableCheckbox}>
              <CheckboxField
                // className={styles.tableRow}
                checked={areAllIdsSelected(sources?.objects, selectedSources)}
                onChange={() => {toggleAll(sources?.objects, selectedSources)}}
              />
            </td>
            
          );
        }}
        controls={(source) => {
          if (! group.owned_by_workspace) {
            return;
          }
          return (
            <button
              type="button"
              className='tableActionBtn'
              onClick={() => setDeleteConfirmModal({ isActive: true, group: group, ids: [source.id] })}
              data-tooltip-id="delete-source"
              data-tooltip-content={t('Delete')}
              aria-label={t('Delete source')}
            >
              <DeleteIconNew />
            </button>
          );
        }}
        onPageChange={(page) => {
          dispatch({type: 'CHANGE_PAGE', page: page})
        }}
      />}

      

      {editModal.isOpen 
        ? <GroupEditModal
            editModal={editModal}
            handleEdit={handleEdit}
            clearEdit={() => setEditModal({isOpen: false, group: null})}
          /> 
        : ''}

        {currentUser?.is_super_admin === true ? (
          <CopyGroupToWorkspacePopup
            isVisible={copyGroupModal.isActive}
            groupId={group.id}
            onClose={() => setCopyGroupModal({ isActive: false })}
          />
        ) : (
          ''
        )}
        {deleteConfirmModal.isActive ?<Modal
        isVisible={deleteConfirmModal.isActive}
        title={t('Confirm Deletion')}
        content={t('Are you sure you want to delete the selected sources from the group?')}
        footer={
          <>
            <button onClick={handleDeleteSources}>
              {t('Confirm')}
            </button>
            <button onClick={() => setDeleteConfirmModal({ isActive: false, group: null, ids: [] })}>
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() => setDeleteConfirmModal({ isActive: false, group: null, ids: [] })}
      /> : ''}
      {copySelectedToGroupModal.isActive && (
        <AddToSourceGroupModal
          isOpen={copySelectedToGroupModal.isActive}
          sources={copySelectedToGroupModal.sources}
          onChange={(isOpen) => setCopySelectedToGroupModal({ isActive: isOpen, sources: [] })}
          onSuccess={clearAllSelectedSources}
        />
      )}
      <ReactTooltip id="delete-source" place="bottom"/>
    </div>
  );
}
